import { ExternalLink, Stethoscope } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type { DiscriminatedProviderTreatmentStatusChangedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface ProviderTreatmentStatusChangedEventProps {
  event: DiscriminatedProviderTreatmentStatusChangedEvent;
  matterId: string;
}

export const ProviderTreatmentStatusChangedEvent: React.FC<
  ProviderTreatmentStatusChangedEventProps
> = ({ event, matterId }) => {
  const { payload, occurred_at, user, agent } = event;
  const { provider, old_treatment_status, new_treatment_status } = payload;

  // Get provider name
  const providerName = provider.name || 'Unknown Provider';

  // Get provider type
  const providerType = provider.provider_type
    ? formatProviderType(provider.provider_type)
    : null;

  // Format treatment statuses
  const oldStatusLabel = formatTreatmentStatus(old_treatment_status);
  const newStatusLabel = formatTreatmentStatus(new_treatment_status);

  // Determine badge variant based on new status
  const newStatusVariant = getTreatmentStatusVariant(new_treatment_status);

  return (
    <EventContainer>
      <EventHeader
        icon={<Stethoscope size={20} />}
        title="Provider Treatment Status Changed"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Provider Name and Type */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{providerName}</span>
            {providerType && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{providerType}</Badge>
              </>
            )}
          </div>

          {/* Address */}
          {provider.address && (
            <div className="text-xs">
              <span className="text-slate-600">Address:</span>{' '}
              <span className="text-[11px]">{provider.address}</span>
            </div>
          )}

          {/* Treatment */}
          {provider.treatment && (
            <div className="text-xs">
              <span className="text-slate-600">Treatment:</span>{' '}
              <span>{provider.treatment}</span>
            </div>
          )}

          {/* Treatment Status Change */}
          <div className="flex items-center gap-2 text-xs mt-2">
            <span className="text-slate-600">Treatment Status:</span>
            <Badge variant="outline" className="line-through">
              {oldStatusLabel}
            </Badge>
            <span className="text-slate-400">→</span>
            <Badge variant={newStatusVariant}>{newStatusLabel}</Badge>
          </div>
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/treatment/${provider.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Provider
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatProviderType(type: string): string {
  const types: Record<string, string> = {
    ORGANIZATION: 'Organization',
    PRACTITIONER: 'Practitioner',
  };
  return types[type] || type;
}

function formatTreatmentStatus(status: string): string {
  const statuses: Record<string, string> = {
    TREATING: 'Treating',
    COMPLETED: 'Completed',
    DEVIATING: 'Deviating',
    UNKNOWN: 'Unknown',
  };
  return statuses[status] || status;
}

function getTreatmentStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'TREATING':
      return 'secondary'; // Blue for active treatment
    case 'COMPLETED':
      return 'default'; // Green for completed
    case 'DEVIATING':
      return 'destructive'; // Red for deviating
    case 'UNKNOWN':
      return 'outline'; // Gray for unknown
    default:
      return 'outline';
  }
}
